import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Calendar, MapPin, Users, Star, Clock, Loader, CheckCircle,
  AlertCircle, Megaphone, ExternalLink, Building2, User,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SkillConfidenceBadge from "@/components/SkillConfidenceBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMAT_LABELS = {
  open_play: "Open Play", doubles: "Doubles Match", practice: "Practice / Drill",
  mixer: "Skill-Matched Mixer", ladder_lite: "Ladder-Lite", doubles_rotation: "Doubles Rotation",
  match_night: "Match Night", drill_social: "Drill Social",
};

export default function GroupSessionDetailPage() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinState, setJoinState] = useState(null); // joined | waitlisted

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/group-sessions/${sessionId}`, { credentials: "include" });
      if (!r.ok) { setError("Session not found."); setLoading(false); return; }
      setSession(await r.json());
    } catch { setError("Failed to load session."); }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async () => {
    if (!user) { navigate(`/login?returnTo=${encodeURIComponent(`/group-sessions/${sessionId}`)}`); return; }
    setJoining(true); setError("");
    try {
      const r = await fetch(`${API}/group-sessions/${sessionId}/join`, { method: "POST", credentials: "include" });
      const data = await r.json();
      if (data.requires_payment) {
        const co = await fetch(`${API}/payments/checkout`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ lookup_key: "group_session_5", quantity: 1, origin_url: window.location.origin, user_id: user?.id, metadata: { session_id: sessionId } }),
        });
        const coData = await co.json();
        if (coData.checkout_url) window.location.href = coData.checkout_url;
        return;
      }
      if (data.status === "joined") setJoinState("joined");
      else if (data.status === "waitlisted") setJoinState("waitlisted");
      await load();
    } catch { setError("Failed to join. Please try again."); }
    finally { setJoining(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center dark-section"><Loader size={28} className="text-brand-green animate-spin" /></div>;
  if (error && !session) return (
    <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center px-4 dark-section">
      <div className="text-center">
        <p className="text-gray-300 mb-4">{error}</p>
        <Link to="/group-sessions" className="text-brand-green hover:underline text-sm">Back to sessions</Link>
      </div>
    </div>
  );

  const s = session;
  const formatKey = s.format || s.session_type || "open_play";
  const cap = s.capacity || 0;
  const filled = s.participants_count || 0;
  const isFull = s.is_full;
  const pct = cap ? Math.min(100, Math.round((filled / cap) * 100)) : 0;
  const price = s.price > 0 ? `$${s.price}` : "Free";
  const announcements = [...(s.announcements || [])].reverse();

  return (
    <div className="min-h-screen bg-[#0F1629] pt-20 pb-16 dark-section" data-testid="group-session-detail">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/group-sessions" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4" data-testid="back-to-sessions-btn">
          <ArrowLeft size={15} /> All sessions
        </Link>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-6 dark-section">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-300 border border-white/10">{FORMAT_LABELS[formatKey] || formatKey}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green border border-brand-green/30 capitalize">{s.sport}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${s.price > 0 ? "bg-brand-orange/10 text-brand-orange border-brand-orange/30" : "bg-green-500/10 text-green-400 border-green-400/30"}`}>{price}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-heading font-black text-white mb-4">{s.title}</h1>
              <div className="space-y-2 text-sm text-gray-300">
                <p className="flex items-center gap-2"><Calendar size={15} className="text-brand-green" /> {s.date_time}</p>
                <p className="flex items-center gap-2"><MapPin size={15} className="text-brand-green" /> {s.location}</p>
                {(s.skill_min || s.skill_max) && <p className="flex items-center gap-2 capitalize"><Star size={15} className="text-brand-green" /> {s.skill_min} – {s.skill_max}</p>}
                {s.organizer_name && <p className="flex items-center gap-2"><User size={15} className="text-brand-green" /> Hosted by {s.organizer_name}</p>}
              </div>
              {s.description && <p className="text-gray-400 text-sm mt-4 leading-relaxed">{s.description}</p>}
            </div>

            {/* Roster */}
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-6 dark-section">
              <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-4"><Users size={16} className="text-brand-green" /> Who's playing ({s.roster?.length || 0})</h2>
              {(!s.roster || s.roster.length === 0) ? (
                <p className="text-gray-500 text-sm">Be the first to join this session.</p>
              ) : (
                <div className="space-y-2" data-testid="session-roster">
                  {s.roster.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-[#1A2035] dark-section" data-testid="roster-item">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {p.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <Link to={`/profile/${p.id}`} className="text-sm text-white hover:text-brand-green transition-colors truncate block">{p.name}</Link>
                          <p className="text-xs text-gray-500 truncate">{p.city}</p>
                        </div>
                      </div>
                      {p.skill_confidence && <SkillConfidenceBadge level={p.skill_confidence.level} label={p.skill_confidence.label} size="xs" variant="dark" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Announcements */}
            {announcements.length > 0 && (
              <div className="bg-[#161B28] border border-white/8 rounded-2xl p-6 dark-section">
                <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-4"><Megaphone size={16} className="text-brand-orange" /> Announcements</h2>
                <div className="space-y-3">
                  {announcements.map((a, i) => (
                    <div key={i} className="px-3 py-2.5 rounded-xl bg-[#1A2035] border border-white/5 dark-section">
                      <p className="text-sm text-gray-200">{a.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{a.sent_by} · {a.sent_at ? new Date(a.sent_at).toLocaleString() : ""}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Join card */}
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-5 md:sticky md:top-24 dark-section">
              <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                <Users size={15} /> {filled}/{cap} spots
                {s.waitlist_count > 0 && <span className="text-yellow-400">· {s.waitlist_count} waitlisted</span>}
              </div>
              <div className="h-2 rounded-full bg-white/8 overflow-hidden mb-4" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Capacity filled">
                <div className={`h-full rounded-full transition-all ${isFull ? "bg-yellow-400" : "bg-brand-green"}`} style={{ width: `${pct}%` }} />
              </div>
              {joinState === "joined" ? (
                <div className="flex items-center justify-center gap-2 text-sm text-brand-green font-medium py-3" data-testid="detail-joined-indicator"><CheckCircle size={16} /> You're in!</div>
              ) : joinState === "waitlisted" ? (
                <div className="flex items-center justify-center gap-2 text-sm text-yellow-400 font-medium py-3" data-testid="detail-waitlisted-indicator"><Clock size={16} /> On waitlist</div>
              ) : (
                <button onClick={handleJoin} disabled={joining}
                  className={`w-full py-3 text-sm font-bold rounded-xl transition-all disabled:opacity-50 ${isFull ? "bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20" : "bg-brand-green text-brand-navy hover:bg-brand-green/90"}`}
                  data-testid="detail-join-btn">
                  {joining ? <span className="flex items-center justify-center gap-2"><Loader size={14} className="animate-spin" /> Joining…</span> : isFull ? "Join Waitlist" : s.price > 0 ? `Join · ${price}` : "Join Session"}
                </button>
              )}
              {error && <p className="text-xs text-red-400 mt-2 flex items-center gap-1"><AlertCircle size={12} /> {error}</p>}
            </div>

            {/* Facility spotlight */}
            {s.facility && (
              <div className="bg-[#161B28] border border-white/8 rounded-2xl overflow-hidden dark-section" data-testid="session-facility-spotlight">
                {s.facility.has_photo && (
                  <img src={`${API}/facility/photo/${s.facility.id}`} alt={s.facility.name} className="w-full h-32 object-cover" />
                )}
                <div className="p-5">
                  <p className="text-xs text-brand-green font-semibold uppercase tracking-wider mb-1">Venue</p>
                  <h3 className="text-white font-bold flex items-center gap-2"><Building2 size={15} /> {s.facility.name}</h3>
                  {s.facility.address && <p className="text-gray-500 text-xs mt-1">{s.facility.address}</p>}
                  {(s.facility.booking_url || s.facility.website) && (
                    <a href={s.facility.booking_url || s.facility.website} target="_blank" rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-green hover:underline" data-testid="facility-book-now-link">
                      Book a court <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
